import React, { FunctionComponent } from 'react';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import { IColumn } from '@fluentui/react';
import { makeStyles, tokens } from '@fluentui/react-components';
import { CheckmarkCircleRegular } from '@fluentui/react-icons';
import { Link, useParams } from 'react-router-dom';
import { ApolloError } from '@apollo/client';
import getColumns from './ProcessList.config';
import { IProcessInstance } from '../../../utils/types/IProcessInstance';
import StatDateFormats from '../../../utils/types/DateFormats';
import ProcessStatus from '../../../utils/types/ProcessStatus';
import LoadingErrorMessage from '../../common/errorContent/LoadingErrorMessage';
import momentUtc from '../../../utils/DateFormatter';
import ShimmeredDetailsListWrapper from '../../common/DetailsLists/ShimmeredDetailsListWrapper';
import { IProcessTypeParams } from '../../common/ParamTypes';
import { MessagingColors } from '../../../app/common/styles/CommonStyleObjects';

const useStyles = makeStyles({
  completeIcon: {
    color: MessagingColors.successIcon,
    fontSize: tokens.fontSizeBase500,
    marginRight: tokens.spacingHorizontalXL,
    minHeight: '22px',
  },
  completeIconSpacer: {
    display: 'inline-block',
    width: tokens.spacingHorizontalXL,
    marginRight: tokens.spacingHorizontalXL,
    minHeight: '22px',
    flexShrink: 0,
  },
});

interface IProcessListProps {
  processInstances: IProcessInstance[];
  processListName: string;
  error: ApolloError; // make sure to add the message to error
  loading: boolean;
}

const ProcessList: FunctionComponent<IProcessListProps> = (props: IProcessListProps) => {
  const { processInstances, error, loading, processListName } = props;
  const { companyCode, processTypeUri } = useParams<IProcessTypeParams>();
  const styles = useStyles();
  const renderItemColumn = (
    item: IProcessInstance,
    index: number,
    column: IColumn,
  ): JSX.Element => {
    const propertyKey = column.fieldName as keyof IProcessInstance;
    const fieldContent = item[propertyKey];
    const isComplete = item.status === ProcessStatus.Complete;
    switch (column.key) {
      case 'processConfigTitle': {
        return (
          <Stack horizontal verticalAlign="center">
            {isComplete ? (
              <CheckmarkCircleRegular className={styles.completeIcon} />
            ) : (
              <span aria-hidden className={styles.completeIconSpacer} />
            )}
            <span>
              {`${momentUtc(item.processStartDate, StatDateFormats.DaySimple)} - ${fieldContent}`}
            </span>
          </Stack>
        );
      }
      case 'processStartDate': {
        return <span>{`${momentUtc(fieldContent, StatDateFormats.DayBreakout)}`}</span>;
      }
      case 'processEndDate': {
        return <span>{`${momentUtc(fieldContent, StatDateFormats.DayBreakout)}`}</span>;
      }

      default:
        return <span>{fieldContent}</span>;
    }
  };

  // main column link
  const renderProcessTitleLink = (item: IProcessInstance): JSX.Element => (
    <Link to={`/companies/${companyCode}/processes/${processTypeUri}/${item.id}`}>
      {item.processConfigTitle}
    </Link>
  );
  const columns = getColumns({
    renderProcessTitleLink,
  });

  return (
    <>
      <LoadingErrorMessage
        loading={loading}
        error={error}
        actionName="loading the process list data"
      />
      <ShimmeredDetailsListWrapper
        columns={columns}
        gridAriaLabelOverride={` Processes for ${processListName}`}
        items={processInstances || []}
        enableShimmer={loading}
        onRenderItemColumn={renderItemColumn}
        singularListLabel="Process"
        pluralListLabel="Processes"
      />
    </>
  );
};

export default ProcessList;
